import { remove } from "lodash-es";
import defaultTo from "lodash-es/defaultTo";
import find from "lodash-es/find";
import isUndefined from "lodash-es/isUndefined";
import { action, makeAutoObservable } from "mobx";

import type { MouseEventHandler } from "react";
import { v4 as uuid } from "uuid";

import { sendErrorMessage } from "../common/tracking";
import type { BaseStoreType } from "../common/types/BaseStoreType";
import type { ToastType } from "../common/types/toastTypes";
import type { TrackingInfo } from "../common/types/trackingTypes";
import posthogStore from "./posthogStore";

type ToastStoreType = BaseStoreType & {
   toasts: ToastType[];
   addSuccess(title: string, message: string): void;
   addError(title: string, message: string, gaErrorTracking?: TrackingInfo, persist?: boolean): void;
   hasDuplicate(toast: ToastType): boolean;
   removeToast(id: string): void;
   addToast(
      title: string,
      message: string,
      type: "success" | "error",
      persist: boolean,
      hideDelay?: number,
      onClick?: MouseEventHandler
   ): void;
};

const toastStore: ToastStoreType = makeAutoObservable({
   toasts: [],

   addSuccess: (title, message) => {
      toastStore.addToast(title, message, "success", false);
   },

   addError: (title, message, gaErrorTracking, persist = false) => {
      gaErrorTracking && sendErrorMessage(gaErrorTracking);
      toastStore.addToast(title, message, "error", persist);
   },

   hasDuplicate: (toast) => {
      const search = {
         title: toast.title,
         message: toast.message,
         type: toast.type
      };
      const existing = find(toastStore.toasts, search);
      return !isUndefined(existing);
   },

   removeToast: (id: string) => {
      toastStore.toasts = toastStore.toasts.filter((t) => t.id !== id);
   },

   addToast: (title, message, type, persist, hideDelay, onClick) => {
      const id = uuid();
      const duration = defaultTo(hideDelay, message.length > 50 ? 5000 : 3000);

      const toast: ToastType = {
         id,
         title,
         message,
         type,
         persist,
         duration,
         show: false,
         onClick
      };

      // If there is already a toast with this exact title, message and type, we just abort.
      if (toastStore.hasDuplicate(toast)) {
         return;
      }

      toastStore.toasts.push(toast);

      if (type === "error") {
         posthogStore.captureEvent("Error Toast Viewed", { variant: type, title, message, url: window.location.pathname });
      }

      // Removes toast from list of toasts after the duration time
      if (toast.duration > 0 && !persist) {
         // This toast actually cleans up the array 500 ms later, so we don't accumulate hidden Toast elements
         setTimeout(
            action(() => {
               remove(toastStore.toasts, { id });
            }),
            duration + 500
         );
      }
   },

   clearCompanySpecificData: () => {
      toastStore.toasts = [];
   }
} satisfies ToastStoreType);

export default toastStore;
